import { usePrevious } from "@/utils/chat/index";
import { Button, Drawer, Form, Input, Radio, Tooltip, message as toast } from "antd";
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import styles from "./index.module.less";
import IconFont from "@/components/icon";
import { get } from "lodash";
import { ChatMessage, ChatMessageType } from "@/types/chat";
import { connect } from "dvajs";
import { AICHAT_MSG_TIP_KEY, initChatMessageInfo, sleep } from "@/utils/chat/message";
import ScrollView from "@/baseComponents/ScrollView";

const TabTitle = {
  new: {
    key: "new"
  },
  rich: {
    key: "wbrs"
  }
};

// 问题栏
function QuestionTabs(props: any) {
  const {
    onChange,
    disable,
    setting = [],
    pushChatMessage,
    currentEmployee,
    selectedSession,
    llmSetting
  } = props;
  const [newVisible, setNewVisible] = useState(false);
  const [tabList, setTabList] = useState<any[]>([
    { type: "新体验", knowledgeBaseId: "new", icon: "icona-" },
    ...(setting || [])
  ]);
  const [selectItem, setSelectItem] = useState({});
  const [modalType, setModalType] = useState("");
  const selectId = useMemo(() => {
    return (selectItem as any)?.knowledgeBaseId;
  }, [selectItem]);
  const preSelectedId = usePrevious(selectId);
  const [contentForm] = Form.useForm();
  const [formData, setFormData] = useState<any>({});
  const scrollRef: any = useRef(null);


  const textStyles = useMemo(() => {
    return llmSetting?.styles || [];
  }, [llmSetting?.styles]);

  const handleFormDataChange = useCallback(
    (val, type) => {
      setFormData({
        ...formData,
        [type]: val
      });
    },
    [formData]
  );

  const defaultStyle = useMemo(() => {
    return textStyles.length ? textStyles[0]?.styleCode || "" : "";
  }, [textStyles]);

  const setModalShow = useCallback(
    (isShow: boolean, type: string) => {
      setNewVisible(isShow);
      setModalType(type);
      if (isShow && defaultStyle) {
        handleFormDataChange(
          {
            style: defaultStyle
          },
          "ext"
        );
      }
    },
    [handleFormDataChange, defaultStyle]
  );

  const handleClick = useCallback(
    (item) => {
      if (item?.knowledgeBaseId === "new") {
        setModalShow(true, "new");
        return;
      }
      if (item?.knowledgeBaseId === "wbrs") {
        setModalShow(true, "rich");
        return;
      }
      if (disable) {
        return;
      }
      setSelectItem(item);
    },
    [disable, setModalShow]
  );

  const handleSelected = useCallback(
    (item: any) => {
      if (item?.knowledgeBaseId === "wbrs") {
        setModalShow(true, "rich");
        return;
      } else {
        setSelectItem(item);
        setNewVisible(false);
      }
    },
    [setModalShow]
  );

  const buildMessage = useCallback(
    (content: string, opts: any) => {
      const messageBody = {
        type: ChatMessageType.TEXT,
        ...opts,
        content,
        question: content,
        isMyself: true
      };
      const message: Partial<ChatMessage> = initChatMessageInfo({
        selectedSession,
        currentEmployee,
        message: messageBody
      });
      return message;
    },
    [currentEmployee, selectedSession]
  );

  const onContentClose = useCallback(() => {
    setNewVisible(false);
    contentForm.resetFields();
  }, [contentForm]);

  const handleConfirmContent = useCallback(() => {
    if (!formData?.content) {
      toast.warn({
        content: "请输入内容",
        key: AICHAT_MSG_TIP_KEY
      });
      return;
    }
    const content = `帮我润色以下内容：\r\n${formData.content}`;
    if (formData.ext) {
      formData.ext = typeof formData.ext === "string" ? formData.ext : JSON.stringify(formData.ext);
    }
    const message = buildMessage(content, formData);
    pushChatMessage({ sessionId: selectedSession.sessionId, message });
    onContentClose();
  }, [formData, onContentClose, buildMessage, pushChatMessage, selectedSession]);

  // 发送问题
  const sendQuestion = useCallback(
    (item: any) => {
      const content = `${item.prompt}`;
      const message = buildMessage(content, {});
      pushChatMessage({ sessionId: selectedSession.sessionId, message });
    },
    [buildMessage, pushChatMessage, selectedSession.sessionId]
  );

  useEffect(() => {
    if (selectId && preSelectedId !== selectId) {
      sendQuestion(selectItem);
      onChange(selectItem);
      sleep(1000).then(() => {
        setSelectItem({});
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preSelectedId, selectId, selectItem, sendQuestion]);

  const titleComp = useMemo(() => {
    const key = get(TabTitle, modalType)?.key;
    const item = tabList.find((i: any) => i.knowledgeBaseId === key);
    if (item) {
      return (
        <>
          <IconFont type={item?.icon} />
          <span>{item?.type}</span>
        </>
      );
    } else {
      return null;
    }
  }, [modalType, tabList]);

  const footerComp = useMemo(() => {
    return modalType === "new" ? null : (
      <div className={styles["rich-footer-wrap"]}>
        <Button onClick={onContentClose}>取消</Button>
        <Button type="primary" onClick={handleConfirmContent}>
          确定
        </Button>
      </div>
    );
  }, [handleConfirmContent, modalType, onContentClose]);

  const richContent = useMemo(() => {
    return (
      <div className={styles["rich-content"]}>
        <Form layout="vertical" autoComplete="off" form={contentForm}>
          <Form.Item name="content" label="润色内容">
            <Input.TextArea
              placeholder="输入内容或直接复制黏贴到输入框"
              maxLength={1900}
              autoSize={{ minRows: 12 }}
              onChange={(e) => handleFormDataChange(e?.target?.value, "content")}
            />
            <p className="count">
              <span>{formData?.content?.length || 0}</span>
              /1900
            </p>
          </Form.Item>
          <Form.Item name="style" label="风格选择">
            <Radio.Group
              onChange={(e) =>
                handleFormDataChange(
                  {
                    style: e?.target?.value
                  },
                  "ext"
                )
              }
              defaultValue={defaultStyle}
            >
              {textStyles.map((item: any) => {
                return (
                  <Radio key={item.styleCode} value={item.styleCode}>
                    {item.styleDesc}
                  </Radio>
                );
              })}
            </Radio.Group>
          </Form.Item>
        </Form>
      </div>
    );
  }, [contentForm, formData?.style, formData?.content?.length, handleFormDataChange, textStyles]);

  const newsContent = useMemo(() => {
    return (
      <div className={styles["new-content"]}>
        {setting?.map((item: any) => {
          return (
            <div
              className={styles["card-wrap"]}
              key={item.knowledgeBaseId}
              onClick={() => handleSelected(item)}
            >
              <div className={styles["title-wrap"]}>
                <IconFont type={item.icon} className={styles["icon"]} />
                <span className={styles["name"]}>{item.type}</span>
              </div>
              <span className={`${styles["text"]} four-lines`}>{item.introduce}</span>
            </div>
          );
        })}
      </div>
    );
  }, [handleSelected, setting]);

  const drawContent = useMemo(() => {
    if (modalType === "rich") {
      return richContent;
    } else if (modalType === "new") {
      return newsContent;
    }
  }, [modalType, newsContent, richContent]);

  return (
    <>
      <div className={styles["chat-action-wrap"]}>
        <ScrollView ref={scrollRef} autoHide scrollBarSize="4px">
          <div className={styles["content"]}>
            {tabList.map((item: any) => {
              return (
                <div
                  onClick={() => handleClick(item)}
                  className={`${styles["item"]} ${
                    item.knowledgeBaseId === selectId ? styles["selected"] : ""
                  }`}
                  key={item.knowledgeBaseId}
                >
                  <Tooltip key="10" title={item.type} placement="top">
                    <div className={styles["text-wrap"]}>
                      <IconFont type={item.icon} className={styles["icon"]} />
                      <span>{item.type}</span>
                    </div>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </ScrollView>
      </div>
      <Drawer
        visible={newVisible}
        getContainer={false}
        placement="bottom"
        title={titleComp}
        closable={false}
        onClose={() => {
          setNewVisible(false);
        }}
        height={540}
        className={styles["new-drawer"]}
        footer={footerComp}
      >
        {drawContent}
      </Drawer>
    </>
  );
}

function mapStateToProps({ chat }: any) {
  return {
    selectedSession: chat.selectedSession,
    currentEmployee: chat.currentEmployee,
    llmSetting: chat.llmSetting
  };
}

function mapDispatchToProps(dispatch: any) {
  return {
    pushChatMessage (data: { sessionId: string; message: Partial<ChatMessage> }) {
      dispatch({ type: "chat/pushChatMessage", payload: data });
    }
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(QuestionTabs);
